import Drawer from '@mui/material/Drawer';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { MetaAdSetWithInsights, MetaAdWithInsights } from 'services/api/metaInsights.api';
import { RADIUS_CARD, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import { DEFAULT_META_DATE_PRESET, type MetaDatePreset, metaDatePresetToRange } from 'utils/metaDatePresets';

import CascadingSheet, { type BreadcrumbStep } from '../drillDown/CascadingSheet';
import { channelDrillDownRegistry } from '../drillDown/channelDrillDownRegistry';
import MetaAdSetSheet from '../drillDown/MetaAdSetSheet';
import MetaAdSheet from '../drillDown/MetaAdSheet';
import MetaCampaignSheet from '../drillDown/MetaCampaignSheet';
import type { CampaignRow, MetaCampaignRowSource } from '../types';

interface CampaignDetailDrawerProps {
  row: CampaignRow | null;
  open: boolean;
  onClose: () => void;
  datePreset?: MetaDatePreset;
  businessUnitId?: number | null;
}

// Wide enough for the 3-col KPI grid, funnel, and children list to breathe;
// falls back to 95vw on narrow screens.
const DRAWER_WIDTH = 'min(780px, 95vw)';

const truncate = (value: string, max = 32): string => (value.length > max ? `${value.slice(0, max - 1)}…` : value);

const CampaignDetailDrawer: React.FC<CampaignDetailDrawerProps> = ({
  row,
  open,
  onClose,
  datePreset = DEFAULT_META_DATE_PRESET,
  businessUnitId,
}) => {
  const [selectedAdSet, setSelectedAdSet] = useState<MetaAdSetWithInsights | null>(null);
  const [selectedAd, setSelectedAd] = useState<MetaAdWithInsights | null>(null);

  useEffect(() => {
    setSelectedAdSet(null);
    setSelectedAd(null);
  }, [row?.id]);

  const { since, until } = useMemo(() => {
    const range = metaDatePresetToRange(datePreset);
    return { since: range.start.format('YYYY-MM-DD'), until: range.end.format('YYYY-MM-DD') };
  }, [datePreset]);

  const isMeta = row?.channel === 'meta';
  const NonMetaBody = row && !isMeta ? channelDrillDownRegistry[row.channel] : null;
  const metaSource = row && isMeta ? (row.raw as MetaCampaignRowSource) : null;
  const metaConnectionId = metaSource?.enrichment?.metaConnectionId ?? null;
  const currency = metaSource?.enrichment?.currency ?? row?.currency ?? null;

  let activeSheet: React.ReactNode = null;

  if (row && isMeta) {
    if (selectedAd) {
      const trail: BreadcrumbStep[] = [
        { eyebrow: 'Campaña', label: truncate(row.campaignName), onClick: () => setSelectedAdSet(null) },
      ];
      if (selectedAdSet) {
        trail.push({
          eyebrow: 'AdSet',
          label: truncate(selectedAdSet.name),
          onClick: () => setSelectedAd(null),
        });
      }
      activeSheet = (
        <CascadingSheet
          eyebrow="Anuncio"
          title={selectedAd.name ?? selectedAd.id}
          accent="meta"
          trail={trail}
          onBack={() => setSelectedAd(null)}
        >
          <MetaAdSheet
            ad={selectedAd}
            currency={currency}
            businessUnitId={businessUnitId ?? undefined}
            since={since}
            until={until}
          />
        </CascadingSheet>
      );
    } else if (selectedAdSet && metaConnectionId !== null) {
      const trail: BreadcrumbStep[] = [
        { eyebrow: 'Campaña', label: truncate(row.campaignName), onClick: () => setSelectedAdSet(null) },
      ];
      activeSheet = (
        <CascadingSheet
          eyebrow="Conjunto de anuncios"
          title={selectedAdSet.name}
          accent="meta"
          trail={trail}
          onBack={() => setSelectedAdSet(null)}
        >
          <MetaAdSetSheet
            adSet={selectedAdSet}
            metaConnectionId={metaConnectionId}
            currency={currency}
            since={since}
            until={until}
            businessUnitId={businessUnitId ?? undefined}
            selectedAdId={null}
            onSelectAd={(ad) => setSelectedAd(ad)}
          />
        </CascadingSheet>
      );
    } else {
      activeSheet = (
        <CascadingSheet
          eyebrow={`Meta Ads${row.accountName ? ` · ${row.accountName}` : ''}`}
          title={row.campaignName}
          accent="meta"
          onClose={onClose}
        >
          <MetaCampaignSheet
            row={row}
            since={since}
            until={until}
            businessUnitId={businessUnitId ?? undefined}
            selectedAdSetId={null}
            onSelectAdSet={(adSet) => setSelectedAdSet(adSet)}
          />
        </CascadingSheet>
      );
    }
  } else if (row && !isMeta && NonMetaBody) {
    activeSheet = (
      <CascadingSheet
        eyebrow={row.channel === 'whatsapp' ? 'WhatsApp' : row.channel}
        title={row.campaignName}
        accent={row.channel === 'whatsapp' ? 'whatsapp' : 'navy'}
        onClose={onClose}
      >
        <NonMetaBody row={row} datePreset={datePreset} businessUnitId={businessUnitId ?? undefined} />
      </CascadingSheet>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: DRAWER_WIDTH,
            bgcolor: SURFACE_CONTAINER_LOWEST,
            borderTopLeftRadius: RADIUS_CARD,
            borderBottomLeftRadius: RADIUS_CARD,
            overflow: 'hidden',
          },
        },
      }}
    >
      {activeSheet}
    </Drawer>
  );
};

export default CampaignDetailDrawer;
